/**
 * Client half of Web Push: capability checks, permission, and registration.
 *
 * The order matters on iOS. Push is only available to a home-screen web app, and
 * `Notification.requestPermission()` must be called from inside a user gesture —
 * calling it on load returns 'default' with no prompt and no error, which looks
 * exactly like the user declining.
 */

export type PushAvailability =
  | 'ready' // can ask for permission right now
  | 'granted' // already subscribed-able
  | 'denied' // user said no; the web cannot re-ask
  | 'needs-install' // iOS, not yet on the home screen
  | 'unsupported' // no service worker or no Push API

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export function pushAvailability(): PushAvailability {
  if (typeof window === 'undefined') return 'unsupported'
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // On iOS in a Safari tab, PushManager is simply not exposed. The fix is to
    // install, not to ask for permission.
    return isIosDevice() && !isStandaloneDisplay() ? 'needs-install' : 'unsupported'
  }
  if (!('Notification' in window)) return 'unsupported'
  if (isIosDevice() && !isStandaloneDisplay()) return 'needs-install'

  switch (Notification.permission) {
    case 'granted':
      return 'granted'
    case 'denied':
      return 'denied'
    default:
      return 'ready'
  }
}

/**
 * Base64url (as VAPID keys are published) to the bytes the Push API wants.
 *
 * The buffer is allocated explicitly so the result is a `Uint8Array<ArrayBuffer>`:
 * `pushManager.subscribe` requires a real ArrayBuffer-backed view, and the default
 * `new Uint8Array(n)` widens to `ArrayBufferLike`, which no longer matches.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

export interface SubscribeResult {
  ok: boolean
  reason?: PushAvailability | 'no-key' | 'failed'
}

/**
 * Requests permission and registers a subscription with our API.
 *
 * Must be called from a user gesture.
 */
export async function enablePush({
  vapidPublicKey,
  getIdToken,
}: {
  vapidPublicKey: string | null
  getIdToken: () => Promise<string | null>
}): Promise<SubscribeResult> {
  if (!vapidPublicKey) return { ok: false, reason: 'no-key' }

  const availability = pushAvailability()
  if (availability === 'needs-install' || availability === 'unsupported' || availability === 'denied') {
    return { ok: false, reason: availability }
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { ok: false, reason: 'denied' }
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      }))

    return (await registerSubscription(subscription, getIdToken)) ? { ok: true } : { ok: false, reason: 'failed' }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}

/**
 * Uploads the current subscription, if there is one.
 *
 * Called on every app open, not only at subscribe time: `pushsubscriptionchange` is
 * not delivered on iOS, so an app-open refresh is the only reliable way to keep the
 * server's copy current.
 */
export async function refreshSubscription(getIdToken: () => Promise<string | null>): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  if (Notification.permission !== 'granted') return
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) await registerSubscription(subscription, getIdToken)
  } catch {
    // Best effort; the next app open tries again.
  }
}

async function registerSubscription(
  subscription: PushSubscription,
  getIdToken: () => Promise<string | null>,
): Promise<boolean> {
  const token = await getIdToken()
  if (!token) return false

  const response = await fetch('/api/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    // The uid is deliberately not sent: the server takes it from the verified token,
    // so a caller cannot register a device against someone else's account.
    body: JSON.stringify(subscription.toJSON()),
  })
  return response.ok
}
